import { useState } from 'react';
import {
  AlertBanner,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  Select,
  StatCard,
  Toast,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, api, endpoints } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDate } from '../lib/format.js';

interface AssessmentRow {
  id: string;
  instrument: { id: string; code: string; title: string; version: number } | null;
  location: { id: string; code: string; name: string } | null;
  auditor: { id: string; full_name: string | null };
  state: string;
  scheduled_at: string | null;
  submitted_at: string | null;
  total_score: number | null;
  max_score: number | null;
  score_pct: number | null;
  band: string | null;
  spawned_item_count: number;
}

interface ScorecardRow extends AssessmentRow {
  answers: Array<{
    criterion_id: string;
    criterion_code: string;
    criterion_seq: number;
    criterion_text: string;
    criterion_text_id: string;
    criterion_text_en: string;
    conforms: boolean | null;
    score: number | null;
    max_score: number;
    note: string | null;
  }>;
}

interface ComparisonRow {
  location: { id: string; code: string; name: string } | null;
  latest_pct: number | null;
  latest_band: string | null;
  latest_at: string | null;
  previous_pct: number | null;
  delta_pct: number | null;
}

/**
 * AUDIT & ASESMEN — Figma `16:2` · M03 · rute `/audits` · izin AUD MGR.
 *
 * LIMA KONDISI PETA §5.3, DAN DUA DI ANTARANYA TIDAK DAPAT DIBUAT DARI KONSOL
 * -------------------------------------------------------------------------
 * terjadwal · berjalan · selesai · **luring belum tersinkron** · **instrumen sudah ditarik
 * versinya**.
 *
 * Yang keempat adalah keadaan PERANGKAT, bukan keadaan baris: sebuah audit yang tersimpan
 * di ponsel yang belum bertemu jaringan tidak ada di basis data, jadi konsol tidak dapat
 * menampilkannya. Yang dilakukan layar ini adalah menyebutkan keberadaannya sebagai
 * kemungkinan — supaya seorang manajer yang melihat area tanpa audit tidak menyimpulkan
 * auditnya tidak pernah dilakukan.
 *
 * Yang kelima BISA ditampilkan, dan ia ditampilkan: `INSTRUMENT_VERSION_WITHDRAWN` menjadi
 * alasan penjadwalan ditolak, dan pilihan instrumen hanya memuat yang TERBIT.
 *
 * SATU BATAS YANG DINYATAKAN DI LAYAR
 * ----------------------------------
 * Menjawab kriteria dan mengirim audit adalah jalur aplikasi LAPANGAN (peta §5.10, alur 3
 * §6): satu kriteria per layar, foto wajib saat tidak sesuai. Rutenya menjawab `501`, dan
 * layar ini mengatakan itu alih-alih menawarkan tombol "Mulai audit" yang gagal.
 */
export function Audits() {
  const { t, locale, translator, me } = useSession();
  const peranManajer = me?.role_code === 'MGR';
  const data = useResource(
    async () => {
      const [list, instruments, comparison, locations, users] = await Promise.all([
        api.get<{ items: AssessmentRow[] }>('/v1/assessments'),
        api.get<{ items: Array<{ id: string; code: string; title: string; version: number }> }>(
          '/v1/instruments',
        ),
        api.get<{ items: ComparisonRow[] }>('/v1/assessments/comparison'),
        endpoints.locations(),
        /*
          `GET /v1/users` berizin MGR, dan pemakai UTAMA layar ini adalah AUDITOR.

          Permintaannya karena itu TIDAK DIKIRIM sama sekali bila pemanggilnya bukan
          manajer — bukan dikirim lalu `catch`-nya ditelan. Keduanya menghasilkan layar
          yang sama; yang berbeda adalah bahwa yang kedua meninggalkan `403` di konsol
          peramban pada setiap pemuatan, dan sebuah galat yang selalu ada adalah galat yang
          orang belajar abaikan — termasuk yang berikutnya, yang bukan.

          Ditemukan pada tangkapan layar persona AUD, bukan pada uji: seluruh gerbang hijau
          sementara layar Audit memanggil rute yang bukan haknya setiap kali dibuka.
        */
        peranManajer ? endpoints.users() : Promise.resolve({ items: [] }),
      ]);
      return {
        assessments: list.items,
        instruments: instruments.items,
        comparison: comparison.items,
        locations: locations.items,
        users: users.items,
      };
    },
    [peranManajer],
  );

  const [openId, setOpenId] = useState<string | null>(null);
  const scorecard = useResource(
    async () => (openId ? api.get<ScorecardRow>(`/v1/assessments/${openId}/scorecard`) : null),
    [openId],
    { enabled: openId !== null },
  );

  const [scheduling, setScheduling] = useState(false);
  const [form, setForm] = useState({ instrument_id: '', location_id: '', auditor_id: '', when: '' });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function jadwalkan() {
    setBusy(true);
    setProblem(null);
    try {
      await api.post('/v1/assessments', {
        instrument_id: form.instrument_id,
        location_id: form.location_id,
        auditor_id: form.auditor_id,
        scheduled_at: new Date(form.when).toISOString(),
      });
      setToast(t('audits.scheduled'));
      setScheduling(false);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('audits.title')}
      /* Judul dua warna `16:249` — dua kunci, bukan satu kalimat yang dipotong. */
      titleAccent={t('audits.titleAccent')}
      /*
        Subjudul `16:252`. Figma menulisnya sebagai BARIS DATA — instrumen, jumlah area,
        siklus, dan aturan temuan otomatis — bukan sebagai kalimat penjelas. Tiga dari
        empat bagiannya ada di data yang layar ini sudah muat; SIKLUS tidak punya kolom
        di mana pun, dan karena itu ia TIDAK ditulis. Sebuah baris data yang tiga
        perempatnya benar dan seperempatnya dikarang lebih buruk daripada baris yang
        lebih pendek: pembacanya tidak punya cara tahu bagian mana yang mana.
      */
      subtitle={
        data.status === 'ready' && data.data
          ? t('audits.subtitle.data', {
              instrument: data.data.instruments[0]?.code ?? '—',
              version: data.data.instruments[0]?.version ?? 0,
              areas: data.data.locations.length,
            })
          : t('audits.subtitle')
      }
      resource={data}
      moduleCode="M03"
      actions={
        <>
          {/*
            `107:1213` — `Manage instrument`, tombol sekunder di sebelah kiri tombol utama.
            Ia DIMATIKAN, dan itu keputusan yang dicatat: tidak ada satu pun rute yang
            MENGUBAH instrumen. M03 menyediakan `GET /v1/instruments` dan
            `GET /v1/instruments/{id}` dan tidak menyediakan yang lain — jadi tidak ada
            kendali yang dapat membuat kata "manage" benar. Yang dipasang karena itu adalah
            bentuk jujurnya: tombolnya ada seperti di frame, mati, dan keterangannya berdiri
            di sebelahnya tanpa perlu ditekan.
          */}
          <span className="page__action-note">
            <Button variant="secondary" disabled data-figma="audits.kelolaInstrumen">
              {t('audits.manageInstrument')}
            </Button>
            <small>{t('audits.manageInstrument.note')}</small>
          </span>
          <Button variant="primary" onClick={() => setScheduling(true)} data-figma="audits.jadwalkan">
            {t('audits.schedule')}
          </Button>
        </>
      }
    >
      {(loaded) => {
        const submitted = loaded.assessments.filter((a) => a.submitted_at !== null);
        /**
         * LEWAT JADWAL — dijadwalkan sebelum sekarang dan belum terkirim. Definisinya
         * ditulis SEKALI di sini dan dipakai kartu `16:271` maupun lencana `107:342`,
         * supaya angka di kartu dan angka di lencana tidak dapat berbeda.
         */
        const sekarang = Date.now();
        const lewatJadwal = loaded.assessments.filter(
          (a) =>
            a.submitted_at === null &&
            a.scheduled_at !== null &&
            Date.parse(a.scheduled_at) < sekarang,
        );
        const avg =
          submitted.length === 0
            ? null
            : Math.round(
                (submitted.reduce((n, a) => n + (a.score_pct ?? 0), 0) / submitted.length) * 10,
              ) / 10;
        return (
          <>
            {problem ? (
              <AlertBanner
                kind="danger"
                signalWord={t('signal.danger')}
                title={renderProblem(translator, problem.problem)}
              />
            ) : null}

            <div className="stat-grid" data-figma="audits.kartu">
              <StatCard label={t('audits.stat.total')} value={String(loaded.assessments.length)} />
              <StatCard
                label={t('audits.stat.average')}
                value={avg === null ? '—' : t('audits.pct', { pct: avg })}
                caption={avg === null ? t('audits.stat.average.none') : undefined}
              />
              {/*
                `16:271` — kartu KEDUA Figma adalah `Areas past schedule`, bukan jumlah
                audit terkirim. Bedanya bukan penamaan: "terkirim" adalah angka yang selalu
                naik dan tidak pernah menuntut tindakan, sementara "lewat jadwal" adalah
                satu-satunya angka di baris ini yang meminta seseorang berbuat sesuatu hari
                ini. Itu sebabnya frame memberinya lencana `attention`.
              */}
              <StatCard
                label={t('audits.stat.pastSchedule')}
                value={String(lewatJadwal.length)}
                trend={lewatJadwal.length > 0 ? t('audits.stat.pastSchedule.trend') : undefined}
                trendDirection={lewatJadwal.length > 0 ? 'down' : 'flat'}
              />
              <StatCard
                label={t('audits.stat.spawned')}
                value={String(loaded.assessments.reduce((n, a) => n + a.spawned_item_count, 0))}
                caption={t('audits.stat.spawned.caption')}
              />
            </div>

            <section className="card" data-figma="audits.jadwal">
              {/*
                `16:286` — judul kartu MEMBAWA LENCANA jumlah lewat jadwal di sebelahnya.
                Angkanya definisi yang sama dengan kartu `16:271`; lencana ada di sini
                supaya orang yang menggulir langsung ke tabel tetap melihatnya.
              */}
              <header className="card__head">
                <h2 className="card__title">{t('audits.list')}</h2>
                {lewatJadwal.length > 0 ? (
                  <span data-figma="audits.jadwal.lencana">
                    <Badge tone="danger">
                      {t('audits.pastScheduleBadge', { count: lewatJadwal.length })}
                    </Badge>
                  </span>
                ) : null}
              </header>
              {loaded.assessments.length === 0 ? (
                <EmptyState
                  title={t('audits.empty.title')}
                  body={t('audits.empty.body')}
                  action={{ label: t('audits.schedule'), onClick: () => setScheduling(true) }}
                />
              ) : (
                /*
                  LIMA KOLOM, bukan enam — Figma `16:290`: Area, Instrument, Auditor, Date,
                  Status. Skor TIDAK punya kolomnya sendiri; ia dilipat ke dalam lencana
                  status (`Completed · 84`), dan itu bukan penghematan tempat: sebuah skor
                  tanpa keadaan audit yang menghasilkannya tidak dapat dibaca — 84 pada
                  audit yang batal berarti hal yang sangat berbeda dari 84 pada audit yang
                  selesai.
                */
                <table className="table" data-figma="audits.tabel">
                  <thead>
                    <tr>
                      <th scope="col">{t('audits.column.area')}</th>
                      <th scope="col">{t('audits.column.instrument')}</th>
                      <th scope="col">{t('audits.column.auditor')}</th>
                      <th scope="col">{t('audits.column.when')}</th>
                      <th scope="col">{t('audits.column.state')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loaded.assessments.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <button className="linkish" type="button" onClick={() => setOpenId(a.id)}>
                            {a.location ? `${a.location.code} — ${a.location.name}` : '—'}
                          </button>
                        </td>
                        <td>
                          {a.instrument?.code ?? '—'}
                          <br />
                          <span className="card__muted">
                            {t('audits.instrumentVersion', { version: a.instrument?.version ?? 0 })}
                          </span>
                        </td>
                        <td>{a.auditor.full_name ?? '—'}</td>
                        <td>
                          {formatDate(a.submitted_at ?? a.scheduled_at ?? '', locale) || '—'}
                        </td>
                        <td>
                          <Badge
                            badgeTone={
                              a.submitted_at === null &&
                              a.scheduled_at !== null &&
                              Date.parse(a.scheduled_at) < sekarang
                                ? 'DANGER'
                                : toneOfAssessment(a.state)
                            }
                          >
                            {a.submitted_at === null &&
                            a.scheduled_at !== null &&
                            Date.parse(a.scheduled_at) < sekarang
                              ? t('audits.state.PAST_SCHEDULE')
                              : a.score_pct === null
                                ? t(`audits.state.${a.state}`)
                                : t('audits.stateWithScore', {
                                    state: t(`audits.state.${a.state}`),
                                    pct: a.score_pct,
                                  })}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {openId !== null && scorecard.status === 'ready' && scorecard.data ? (
              <section className="card">
                <div className="page__head">
                  <h2 className="card__title">
                    {t('audits.scorecard', {
                      area: scorecard.data.location
                        ? `${scorecard.data.location.code} — ${scorecard.data.location.name}`
                        : '—',
                    })}
                  </h2>
                  <Button variant="text" onClick={() => setOpenId(null)}>
                    {t('app.close')}
                  </Button>
                </div>
                <p>
                  {t('audits.scorecard.total', {
                    total: scorecard.data.total_score ?? 0,
                    max: scorecard.data.max_score ?? 0,
                    pct: scorecard.data.score_pct ?? 0,
                  })}{' '}
                  <Badge badgeTone={toneOfBand(scorecard.data.band)}>
                    {t(`audits.band.${scorecard.data.band ?? 'MERAH'}`)}
                  </Badge>
                </p>
                {scorecard.data.answers.length === 0 ? (
                  <p className="card__muted">{t('audits.scorecard.noAnswers')}</p>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">{t('audits.column.criterion')}</th>
                        <th scope="col">{t('audits.column.conforms')}</th>
                        <th scope="col">{t('audits.column.score')}</th>
                        <th scope="col">{t('audits.column.note')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scorecard.data.answers.map((ans) => (
                        <tr key={ans.criterion_id}>
                          <td>{ans.criterion_seq}</td>
                          <td>
                            {locale === 'id-ID' ? ans.criterion_text_id : ans.criterion_text_en}
                          </td>
                          <td>
                            <Badge badgeTone={ans.conforms === false ? 'WARNING' : 'SUCCESS'}>
                              {t(ans.conforms === false ? 'audits.notConform' : 'audits.conform')}
                            </Badge>
                          </td>
                          <td>
                            {ans.score ?? 0} / {ans.max_score}
                          </td>
                          <td>{ans.note ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            ) : null}

            <section className="card">
              <h2 className="card__title">{t('audits.comparison')}</h2>
              {loaded.comparison.length === 0 ? (
                <p className="card__muted">{t('audits.comparison.empty')}</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('audits.column.area')}</th>
                      <th scope="col">{t('audits.column.latest')}</th>
                      <th scope="col">{t('audits.column.previous')}</th>
                      <th scope="col">{t('audits.column.delta')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loaded.comparison.map((row) => (
                      <tr key={row.location?.id ?? Math.random()}>
                        <td>{row.location ? `${row.location.code} — ${row.location.name}` : '—'}</td>
                        <td>
                          {row.latest_pct === null ? '—' : t('audits.pct', { pct: row.latest_pct })}
                        </td>
                        <td>
                          {row.previous_pct === null
                            ? t('audits.comparison.noPrevious')
                            : t('audits.pct', { pct: row.previous_pct })}
                        </td>
                        <td>
                          {/*
                            `null` berarti BELUM ADA pembanding, dan itu bukan nol. Sebuah
                            panah datar di atas area yang baru pertama kali diaudit
                            menyatakan sesuatu yang tidak diketahui siapa pun.
                          */}
                          {row.delta_pct === null ? (
                            <span className="card__muted">{t('audits.comparison.noPrevious')}</span>
                          ) : (
                            <Badge
                              badgeTone={
                                row.delta_pct > 0 ? 'SUCCESS' : row.delta_pct < 0 ? 'WARNING' : 'NEUTRAL'
                              }
                            >
                              {t('audits.delta', { delta: row.delta_pct })}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Dua batas yang dinyatakan, bukan disembunyikan. */}
            <p className="card__muted">{t('audits.offlineNote')}</p>
            <p className="card__muted">{t('audits.fieldOnlyNote')}</p>

            <Dialog
              open={scheduling}
              kind="confirm"
              title={t('audits.schedule')}
              description={t('audits.schedule.describe')}
              confirmLabel={t('app.save')}
              cancelLabel={t('app.cancel')}
              busy={busy}
              confirmDisabled={
                form.instrument_id === '' ||
                form.location_id === '' ||
                form.auditor_id === '' ||
                form.when === ''
              }
              onConfirm={() => void jadwalkan()}
              onCancel={() => setScheduling(false)}
            >
              <Select
                label={t('audits.column.instrument')}
                value={form.instrument_id}
                onChange={(e) => setForm({ ...form, instrument_id: e.target.value })}
                options={[
                  { value: '', label: t('report.field.choose') },
                  ...loaded.instruments.map((i) => ({
                    value: i.id,
                    label: `${i.code} v${i.version} — ${i.title}`,
                  })),
                ]}
              />
              <Select
                label={t('audits.column.area')}
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                options={[
                  { value: '', label: t('report.field.choose') },
                  ...loaded.locations
                    .filter((l) => l.state === 'ACTIVE')
                    .map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
                ]}
              />
              {/*
                Ketika daftar pengguna tidak terbaca (auditor tidak berhak membacanya),
                satu-satunya auditor yang dapat ditugaskan adalah DIRINYA SENDIRI — dan itu
                dikatakan, bukan ditampilkan sebagai daftar kosong yang membuat tombol
                simpan mati tanpa alasan.
              */}
              <Select
                label={t('audits.column.auditor')}
                value={form.auditor_id}
                onChange={(e) => setForm({ ...form, auditor_id: e.target.value })}
                hint={loaded.users.length === 0 ? t('audits.auditorSelfOnly') : undefined}
                options={
                  loaded.users.length === 0
                    ? [{ value: me?.id ?? '', label: me?.full_name ?? '' }]
                    : [
                        { value: '', label: t('report.field.choose') },
                        ...loaded.users.map((u) => ({
                          value: u.id,
                          label: u.full_name ?? u.id,
                        })),
                      ]
                }
              />
              <Input
                label={t('audits.schedule.when')}
                inputType="datetime-local"
                value={form.when}
                onChange={(e) => setForm({ ...form, when: e.target.value })}
              />
            </Dialog>

            {toast ? (
              <div className="assist-toast-stack">
                <Toast
                  kind="success"
                  signalWord={t('signal.success')}
                  title={toast}
                  onDismiss={() => setToast(null)}
                  closeLabel={t('app.dismiss')}
                />
              </div>
            ) : null}
          </>
        );
      }}
    </Screen>
  );
}

function toneOfAssessment(state: string): string {
  switch (state) {
    case 'SUBMITTED':
      return 'SUCCESS';
    case 'IN_PROGRESS':
      return 'INFORMATION';
    case 'VOIDED':
      return 'NEUTRAL';
    default:
      return 'NEUTRAL';
  }
}

function toneOfBand(band: string | null): string {
  switch (band) {
    case 'HIJAU':
      return 'SUCCESS';
    case 'KUNING':
      return 'WARNING';
    case 'MERAH':
      return 'DANGER';
    default:
      return 'NEUTRAL';
  }
}
